import React, { useState, useRef, useEffect } from "react";
import { terminalAPI } from "../../services/api";
import { usePOSCart, usePOSPayment } from "../../context/POSContext";
import "./POSCart.css";

const POSCart = ({ disabled, onCustomerLookup }) => {
  const {
    items,
    totals,
    customer,
    appliedDiscount,
    updateQuantity,
    removeItem,
    clearCart,
    updateItems,
    clearCustomer,
    applyDiscount,
    removeDiscount,
  } = usePOSCart();

  const { processPayment } = usePOSPayment();

  const [customerEmail, setCustomerEmail] = useState("");
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(null);
  const [applyingPromoCode, setApplyingPromoCode] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState(null);

  // Format price from cents to dollars
  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handleQuantityChange = (productId, e) => {
    const newQuantity = parseInt(e.target.value, 10);
    if (!isNaN(newQuantity) && newQuantity > 0) {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleEmailToggle = () => {
    setShowEmailInput(!showEmailInput);
    if (!showEmailInput) {
      setCustomerEmail("");
    }
  };

  // Apply promo code
  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoCodeError("Please enter a promo code");
      return;
    }

    setApplyingPromoCode(true);
    setPromoCodeError(null);

    try {
      const response = await terminalAPI.applyDiscount({
        code: promoCode,
        amount: totals.subtotal,
        cartItems: items,
        // Optional: Include product type if needed
        productType: items.length > 0 ? items[0].productType : undefined,
      });

      // Check if response is successful
      if (response.data.success) {
        // Extract discount information from the correct location in the response
        // Handle both response formats (from terminalController and promoCodeController)
        const discountData = response.data.data?.discount || response.data;
        const discountAmount = Math.abs(discountData.discountAmount || 0); // Ensure positive value
        const appliedCode =
          response.data.data?.promoCode?.code || response.data.discountCode;

        // Store the applied promo code info
        setPromoCodeApplied({
          code: appliedCode,
          discountAmount: discountAmount,
          message: response.data.message || discountData.message,
        });

        // Apply discount using context
        applyDiscount({
          code: appliedCode,
          amount: discountAmount,
        });

        // Update cart items with discounted prices returned from the backend
        if (response.data.items && Array.isArray(response.data.items)) {
          // Replace the current items with the discounted items
          updateItems(response.data.items);
        }
        // Clear the input
        setPromoCode("");
      } else {
        setPromoCodeError(
          response.data.message || "Failed to apply promo code"
        );
      }
    } catch (error) {
      setPromoCodeError(
        error.response?.data?.message || "Failed to apply promo code"
      );
    } finally {
      setApplyingPromoCode(false);
    }
  };

  // Remove promo code
  const handleRemovePromoCode = () => {
    setPromoCodeApplied(null);
    setPromoCodeError(null);
    // Remove discount using context
    removeDiscount();
  };

  const handlePayment = () => {
    // Use customer data if available, otherwise use email input
    const paymentData = customer
      ? {
          customerId: customer.customerId,
          customerEmail: customer.customerEmail,
        }
      : customerEmail
      ? { customerEmail }
      : {};

    // Add promo code info to payment data if applied
    if (promoCodeApplied) {
      paymentData.discountCode = promoCodeApplied.code;
      paymentData.discountAmount = promoCodeApplied.discountAmount;
    }
    processPayment(paymentData);
  };

  // Ref for cart items container to scroll to bottom when new items are added
  const cartItemsRef = useRef(null);

  // Effect to scroll to the bottom of the cart when new items are added
  useEffect(() => {
    if (cartItemsRef.current) {
      // Scroll to the bottom with a small delay to ensure rendering is complete
      setTimeout(() => {
        cartItemsRef.current.scrollTop = cartItemsRef.current.scrollHeight;
      }, 100);
    }
  }, [items.length]); // Only trigger when the number of items changes

  return (
    <div className="pos-cart">
      <div className="pos-cart-header">
        <h2>Shopping Cart</h2>
        {items.length > 0 && (
          <button
            className="pos-clear-cart-btn"
            onClick={clearCart}
            disabled={disabled}
          >
            Clear
          </button>
        )}
      </div>

      {/* Customer section - moved to top of cart */}
      <div className="pos-customer-section">
        {customer ? (
          <div className="pos-selected-customer">
            <div className="pos-customer-info">
              <span className="pos-customer-name">{customer.customerName}</span>
              <span className="pos-customer-email">
                {customer.customerEmail}
              </span>
              {customer.customerPhone && (
                <span className="pos-customer-phone">
                  {customer.customerPhone}
                </span>
              )}
            </div>
            <button
              className="pos-change-customer-btn"
              onClick={clearCustomer}
              disabled={disabled}
            >
              Change
            </button>
          </div>
        ) : (
          <button
            className="pos-lookup-customer-btn"
            onClick={onCustomerLookup}
            disabled={disabled}
          >
            <i className="fas fa-user-plus"></i> Look Up Customer
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="pos-empty-cart">
          <p>Your cart is empty</p>
          <p className="pos-empty-cart-subtext">
            Add products by clicking on items in the product list
          </p>
        </div>
      ) : (
        <>
          <div className="pos-cart-items" ref={cartItemsRef}>
            {items.map((item) => (
              <div
                key={item.productId}
                className="pos-cart-item"
                data-testid={`cart-item-${item.productId}`}
              >
                <div className="pos-cart-item-details">
                  <div>
                    <div className="pos-cart-item-name">{item.name}</div>
                    {item.category && (
                      <div className="pos-cart-item-category">
                        {item.category}
                      </div>
                    )}
                  </div>
                  <div className="pos-cart-item-price">
                    {item.isProrated ? (
                      <>
                        <div className="pos-cart-item-price-details">
                          <span className="pos-cart-item-original-price">
                            Original: {formatPrice(item.originalPrice)}
                          </span>
                          <span className="pos-cart-item-prorated-price">
                            Prorated: {formatPrice(item.price)}
                          </span>
                        </div>
                        <div className="pos-cart-item-proration-info">
                          <span className="pos-cart-item-next-billing">
                            Next billing:{" "}
                            {new Date(
                              item.nextBillingDate
                            ).toLocaleDateString()}
                          </span>
                          <span className="pos-cart-item-proration-note">
                            You are only paying for the remaining days in the
                            current billing period.
                          </span>
                        </div>
                      </>
                    ) : (
                      <>{formatPrice(item.unitPrice)} each</>
                    )}
                    {item.priceLabel && (
                      <span className="pos-cart-item-price-label">
                        {item.priceLabel}
                      </span>
                    )}
                  </div>
                </div>
                <div className="pos-cart-item-actions">
                  <div className="pos-cart-item-quantity">
                    <button
                      onClick={() =>
                        updateQuantity(item.productId, item.quantity - 1)
                      }
                      disabled={item.quantity <= 1 || disabled}
                      className="pos-quantity-btn"
                      aria-label="Decrease quantity"
                      title="Decrease quantity"
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleQuantityChange(item.productId, e)}
                      disabled={disabled}
                      className="pos-quantity-input"
                      aria-label="Item quantity"
                    />
                    <button
                      onClick={() =>
                        updateQuantity(item.productId, item.quantity + 1)
                      }
                      disabled={disabled}
                      className="pos-quantity-btn"
                      aria-label="Increase quantity"
                      title="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                  <div className="pos-cart-item-total">
                    {formatPrice(item.totalPrice)}
                  </div>
                  <button
                    onClick={() => removeItem(item.productId)}
                    disabled={disabled}
                    className="pos-remove-item-btn"
                    aria-label="Remove item"
                    title="Remove item"
                  >
                    ×
                  </button>
                </div>
                {item.discounted && (
                  <div className="pos-cart-item-discount">
                    <span className="discount-label">Discounted</span>
                    <span className="discount-amount">
                      -{formatPrice(item.discountAmount)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Promo Code Section */}
          <div className="pos-promo-code-section">
            {promoCodeApplied ? (
              <div className="pos-applied-promo">
                <div className="pos-promo-info">
                  <span className="pos-promo-label">Promo Code Applied:</span>
                  <span className="pos-promo-code">
                    {promoCodeApplied.code}
                  </span>
                  <span className="pos-promo-discount">
                    -{formatPrice(promoCodeApplied.discountAmount)}
                  </span>
                </div>
                <button
                  className="pos-remove-promo-btn"
                  onClick={handleRemovePromoCode}
                  disabled={disabled}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="pos-promo-input">
                <input
                  type="text"
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  disabled={disabled || applyingPromoCode}
                  className={promoCodeError ? "error" : ""}
                />
                <button
                  className="pos-apply-promo-btn"
                  onClick={handleApplyPromoCode}
                  disabled={disabled || applyingPromoCode || !promoCode.trim()}
                >
                  {applyingPromoCode ? "Applying..." : "Apply"}
                </button>
              </div>
            )}
            {promoCodeError && (
              <div className="pos-promo-error">{promoCodeError}</div>
            )}
          </div>

          <div className="pos-cart-summary">
            <div className="pos-cart-summary-inner">
              <div className="pos-cart-subtotal">
                <span>Subtotal:</span>
                <span>{formatPrice(totals.subtotal)}</span>
              </div>
              {appliedDiscount && (
                <div className="pos-cart-discount">
                  <span>Discount ({appliedDiscount.code}):</span>
                  <span>-{formatPrice(appliedDiscount.amount || 0)}</span>
                </div>
              )}
              <div className="pos-cart-tax">
                <span>Tax (8.6%):</span>
                <span>{formatPrice(totals.taxAmount)}</span>
              </div>
              <div className="pos-cart-total">
                <span>Total:</span>
                <span>{formatPrice(totals.total)}</span>
              </div>
            </div>
          </div>

          <div className="pos-cart-actions">
            {/* Only show email receipt option if no customer is selected */}
            {!customer && (
              <div className="pos-email-receipt">
                <button
                  className={`pos-email-toggle ${
                    showEmailInput ? "active" : ""
                  }`}
                  onClick={handleEmailToggle}
                  disabled={disabled}
                >
                  {showEmailInput ? "Cancel Email" : "Email Receipt"}
                </button>
                {showEmailInput && (
                  <input
                    type="email"
                    placeholder="Customer email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    disabled={disabled}
                    className="pos-email-input"
                  />
                )}
              </div>
            )}
            <div className="pos-payment-buttons">
              <button
                className="pos-gift-card-btn"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("openGiftCardPayment"))
                }
                disabled={disabled}
              >
                <i className="fas fa-gift"></i> Gift Card
              </button>
              <button
                className="pos-checkout-btn"
                onClick={handlePayment}
                disabled={disabled}
              >
                {disabled ? (
                  "Processing..."
                ) : (
                  <>
                    <i className="fas fa-credit-card"></i> Process Payment (
                    {formatPrice(totals.total)})
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default POSCart;
